// src/components/asset/GenerationWizard.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel, Button, TextField,
  Grid, FormControl, InputLabel, Select, MenuItem,
  Chip, Box, Typography, Alert, CircularProgress,
  Card, CardContent, Checkbox, FormControlLabel,
  Autocomplete, Paper, Divider, Slider, IconButton,
  Tooltip
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Tune as TuneIcon,
  Handyman as HandymanIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import Gateway from '../../api/gateway';

const steps = {
  auto: ['Параметры', 'Генерация', 'Результат'],
  'semi-auto': ['Базовые параметры', 'Выбор модулей', 'Генерация', 'Результат'],
  manual: ['Параметры', 'Добавление модулей', 'Сохранение']
};

const SIZE_TIERS = ['nano', 'micro', 'small', 'medium', 'large', 'huge', 'capital', 'megastructure'];
const TECH_LEVELS = Array.from({ length: 10 }, (_, i) => i + 1);

const SHIP_CLASSES = [
  { value: 'fighter', label: 'Истребитель' },
  { value: 'bomber', label: 'Бомбардировщик' },
  { value: 'freighter', label: 'Грузовой' },
  { value: 'explorer', label: 'Исследовательский' },
  { value: 'cruiser', label: 'Крейсер' },
  { value: 'battleship', label: 'Линкор' },
  { value: 'carrier', label: 'Авианосец' },
  { value: 'medical', label: 'Медицинский' },
  { value: 'science', label: 'Научный' },
  { value: 'scout', label: 'Разведчик' },
  { value: 'transport', label: 'Транспорт' },
  { value: 'micro_fighter', label: 'Микро-истребитель' },
  { value: 'surface_hopper', label: 'Планетарный' },
  { value: 'custom', label: 'Пользовательский' }
];

const STRUCTURE_TYPES = [
  { value: 'space_station', label: 'Космическая станция' },
  { value: 'military_base', label: 'Военная база' },
  { value: 'surface_settlement', label: 'Поселение' },
  { value: 'building', label: 'Здание' }
];

const VEHICLE_TYPES = [
  { value: 'micro_fighter', label: 'Микро-истребитель' },
  { value: 'surface_hopper', label: 'Планетарный транспорт' },
  { value: 'hover', label: 'Гравитационный' },
  { value: 'wheeled', label: 'Колёсный' },
  { value: 'tracked', label: 'Гусеничный' },
  { value: 'flying', label: 'Летательный' },
  { value: 'shuttle', label: 'Челнок' },
  { value: 'military', label: 'Военный' },
  { value: 'construction', label: 'Строительный' },
  { value: 'mining', label: 'Горнодобывающий' },
  { value: 'scout', label: 'Разведывательный' },
  { value: 'freight', label: 'Грузовой' },
  { value: 'explorer', label: 'Исследовательский' }
];

export default function GenerationWizard({ 
  open, 
  onClose, 
  onComplete, 
  type, 
  mode, 
  moduleLibrary = [],
  initialData = null
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  
  // Параметры для генерации
  const [params, setParams] = useState({
    name: '',
    class: type === 'ship' ? 'freighter' : null,
    structure_type: type === 'structure' ? 'space_station' : null,
    vehicle_type: type === 'vehicle' ? 'hover' : null,
    size_tier: 'medium',
    tech_level: 5,
    description: ''
  });

  // Выбранные модули для полуавтоматической генерации
  const [selectedModules, setSelectedModules] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);

  // Загрузка доступных модулей
  useEffect(() => {
    if (mode === 'semi-auto' && moduleLibrary.length > 0) {
      setAvailableModules(moduleLibrary);
    }
  }, [mode, moduleLibrary]);

  const handleNext = async () => {
    if (activeStep === steps[mode].length - 2) {
      // Последний шаг перед результатом - запускаем генерацию
      await generate();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const generate = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      switch (mode) {
        case 'auto':
          response = await Gateway.api.scheme.generateAuto(type, params);
          break;
        case 'semi-auto':
          const moduleChoices = selectedModules.map(m => ({
            purpose: m.purpose,
            count: m.count || 1,
            specific_type: m.id
          }));
          response = await Gateway.api.scheme.generateSemiAuto(type, params, moduleChoices);
          break;
        case 'manual':
          // Для ручного режима просто переходим к сохранению
          setResult({ ...params, installed_modules: selectedModules });
          setActiveStep(prev => prev + 1);
          setLoading(false);
          return;
        default:
          throw new Error('Unknown generation mode');
      }

      setResult(response);
      setActiveStep(prev => prev + 1);
    } catch (err) {
      console.error('Generation error:', err);
      setError(Gateway.utils.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    onComplete(result);
    reset();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const reset = () => {
    setActiveStep(0);
    setLoading(false);
    setError(null);
    setResult(null);
    setParams({
      name: '',
      class: type === 'ship' ? 'freighter' : null,
      structure_type: type === 'structure' ? 'space_station' : null,
      vehicle_type: type === 'vehicle' ? 'hover' : null,
      size_tier: 'medium',
      tech_level: 5,
      description: ''
    });
    setSelectedModules([]);
  };

  const addModule = (module) => {
    setSelectedModules(prev => [...prev, { ...module, count: 1 }]);
  };

  const removeModule = (moduleId) => {
    setSelectedModules(prev => prev.filter(m => m.id !== moduleId));
  };

  const updateModuleCount = (moduleId, count) => {
    setSelectedModules(prev => 
      prev.map(m => m.id === moduleId ? { ...m, count } : m)
    );
  };

  const renderStepContent = (step) => {
    const stepIndex = step;

    switch (mode) {
      case 'auto':
        return renderAutoSteps(stepIndex);
      case 'semi-auto':
        return renderSemiAutoSteps(stepIndex);
      case 'manual':
        return renderManualSteps(stepIndex);
      default:
        return null;
    }
  };

  const renderAutoSteps = (step) => {
    switch (step) {
      case 0:
        return renderParametersStep();
      case 1:
        return renderGeneratingStep();
      case 2:
        return renderResultStep();
      default:
        return null;
    }
  };

  const renderSemiAutoSteps = (step) => {
    switch (step) {
      case 0:
        return renderParametersStep();
      case 1:
        return renderModuleSelectionStep();
      case 2:
        return renderGeneratingStep();
      case 3:
        return renderResultStep();
      default:
        return null;
    }
  };

  const renderManualSteps = (step) => {
    switch (step) {
      case 0:
        return renderParametersStep();
      case 1:
        return renderModuleSelectionStep(true);
      case 2:
        return renderSaveStep();
      default:
        return null;
    }
  };

  const renderParametersStep = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Название"
          value={params.name}
          onChange={(e) => setParams({ ...params, name: e.target.value })}
          placeholder={`Новый ${type}`}
        />
      </Grid>

      {type === 'ship' && (
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Класс корабля</InputLabel>
            <Select
              value={params.class}
              label="Класс корабля"
              onChange={(e) => setParams({ ...params, class: e.target.value })}
            >
              {SHIP_CLASSES.map(c => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      {type === 'structure' && (
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Тип структуры</InputLabel>
            <Select
              value={params.structure_type}
              label="Тип структуры"
              onChange={(e) => setParams({ ...params, structure_type: e.target.value })}
            >
              {STRUCTURE_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      {type === 'vehicle' && (
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Тип транспорта</InputLabel>
            <Select
              value={params.vehicle_type}
              label="Тип транспорта"
              onChange={(e) => setParams({ ...params, vehicle_type: e.target.value })}
            >
              {VEHICLE_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Размер</InputLabel>
          <Select
            value={params.size_tier}
            label="Размер"
            onChange={(e) => setParams({ ...params, size_tier: e.target.value })}
          >
            {SIZE_TIERS.map(s => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Технологический уровень</InputLabel>
          <Select
            value={params.tech_level}
            label="Технологический уровень"
            onChange={(e) => setParams({ ...params, tech_level: e.target.value })}
          >
            {TECH_LEVELS.map(l => (
              <MenuItem key={l} value={l}>{l}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Описание"
          value={params.description}
          onChange={(e) => setParams({ ...params, description: e.target.value })}
          placeholder="Описание объекта..."
        />
      </Grid>
    </Grid>
  );

  const renderModuleSelectionStep = (allowManualAdd = false) => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Доступные модули
        </Typography>
        <Autocomplete
          options={availableModules}
          getOptionLabel={(opt) => `${opt.name} (${opt.purpose})`}
          renderInput={(params) => (
            <TextField {...params} label="Поиск модулей" size="small" />
          )}
          onChange={(e, value) => {
            if (value) {
              addModule(value);
            }
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          Выбранные модули
        </Typography>
        
        {selectedModules.length === 0 ? (
          <Alert severity="info">Модули не выбраны</Alert>
        ) : (
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {selectedModules.map((module) => (
              <Paper key={module.id} variant="outlined" sx={{ p: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ flex: 1 }}>{module.name}</Typography>
                  <TextField
                    type="number"
                    size="small"
                    value={module.count || 1}
                    onChange={(e) => updateModuleCount(module.id, parseInt(e.target.value) || 1)}
                    inputProps={{ min: 1, max: 10, style: { width: 60 } }}
                  />
                  <IconButton size="small" onClick={() => removeModule(module.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Chip size="small" label={module.purpose} />
                  <Chip size="small" label={`Tech ${module.tech_level}`} />
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Grid>
    </Grid>
  );

  const renderGeneratingStep = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CircularProgress size={60} />
      <Typography variant="h6" sx={{ mt: 2 }}>
        Генерация чертежа...
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );

  const renderResultStep = () => (
    <Box>
      {result ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="success">
              Чертеж успешно создан!
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6">{result.name}</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                ID: {result.id}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption">Тип:</Typography>
                  <Typography>{result.type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption">Размер:</Typography>
                  <Typography>{result.size_tier}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption">Tech Level:</Typography>
                  <Typography>{result.tech_level}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption">Модулей:</Typography>
                  <Typography>{(result.installed_modules || []).length}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="warning">Нет данных для отображения</Alert>
      )}
    </Box>
  );

  const renderSaveStep = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Ручной режим: вы можете сохранить чертеж и продолжить редактирование в основном редакторе.
      </Alert>
      <Typography variant="subtitle1" gutterBottom>
        Выбранные модули: {selectedModules.length}
      </Typography>
      {selectedModules.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
          {selectedModules.map((module) => (
            <Chip
              key={module.id}
              label={`${module.name} (${module.purpose})`}
              size="small"
              sx={{ m: 0.5 }}
            />
          ))}
        </Paper>
      )}
    </Box>
  );

  const currentSteps = steps[mode] || [];
  const isLastStep = activeStep === currentSteps.length - 1;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {mode === 'auto' && <AutoAwesomeIcon />}
          {mode === 'semi-auto' && <TuneIcon />}
          {mode === 'manual' && <HandymanIcon />}
          <Typography variant="h6">
            {mode === 'auto' && 'Автоматическая генерация'}
            {mode === 'semi-auto' && 'Полуавтоматическая генерация'}
            {mode === 'manual' && 'Ручное создание'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {currentSteps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} startIcon={<CloseIcon />}>
          Отмена
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} startIcon={<ArrowBackIcon />}>
            Назад
          </Button>
        )}
        {!isLastStep ? (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForwardIcon />}
            disabled={loading}
          >
            {activeStep === currentSteps.length - 2 ? 'Сгенерировать' : 'Далее'}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            onClick={handleComplete}
            startIcon={<CheckIcon />}
          >
            Готово
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}